import React from 'react';
import {
  Box,
  BoxProps,
  Step,
  stepConnectorClasses,
  StepContent,
  stepContentClasses,
  StepIconProps,
  StepLabel,
  stepLabelClasses,
  Stepper,
  styled,
} from '@mui/material';
import FormikContext from './formik/FormikContext';
import { Check, Info, Warning } from '@mui/icons-material';

const MuiStepperWrap = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,

  '&:before': {
    content: '""',
    position: 'absolute',
    top: '-3px',
    left: '-3px',
    backgroundColor: '#ebeef1',
    height: 'calc(100% + 6px)',
    width: 26,
    zIndex: -1,
    borderRadius: 15,
  },
}));

const MuiStepper = styled(Stepper)(({ theme }) => ({
  [`& .MuiStepIcon-text`]: {
    fontSize: 18,
  },

  [`& .${stepLabelClasses.root}`]: {
    cursor: 'pointer',
    padding: 0,
    paddingBottom: theme.spacing(1.65),

    [`& .${stepLabelClasses.iconContainer}`]: {
      '.MuiSvgIcon-root': {
        fill: '#687385',
        fontSize: 20,
        width: 20,

        '& .MuiStepIcon-text': {
          fill: '#fff',
        },
      },

      '&.Mui-active': {
        '.MuiSvgIcon-root': {
          fill: theme.palette.primary.main,
        },
      },

      '&.Mui-completed': {
        '.MuiSvgIcon-root': {
          fill: theme.palette.success.main,
        },
      },

      '&.Mui-error': {
        '.MuiSvgIcon-root': {
          fill: theme.palette.error.main,
        },
      },
    },

    [`& + .${stepContentClasses.root}`]: {
      '& > .MuiCollapse-root': {
        paddingBottom: theme.spacing(2),
        cursor: 'pointer',
      },
    },
  },

  '& > div:last-child': {
    [`& .${stepLabelClasses.root}`]: {
      paddingBottom: 0,
    },

    [`& .${stepContentClasses.root}`]: {
      '& > .MuiCollapse-root': {
        paddingBottom: 0,
        paddingTop: theme.spacing(1.65),
      },
    },
  },

  [`& .${stepLabelClasses.iconContainer}`]: {
    paddingRight: theme.spacing(2),
  },

  [`& .${stepLabelClasses.label}`]: {
    fontSize: 14,
    color: theme.palette.grey[800],
    fontWeight: 700,
  },

  [`& .${stepContentClasses.root}`]: {
    border: 'none',
    paddingLeft: 0,
    marginLeft: 0,

    [`& .${stepLabelClasses.label}`]: {
      color: theme.palette.grey[600],
      fontWeight: 500,

      '&.Mui-active': {
        fontWeight: 700,
      },
    },

    [`& .${stepLabelClasses.root}`]: {
      [`& .${stepLabelClasses.iconContainer}`]: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.palette.grey[500],
        marginRight: theme.spacing(4.5),
        padding: 0,
        marginLeft: 8,
        border: 'none',
      },
      svg: {
        display: 'none',
      },
    },

    [`& .${stepLabelClasses.active}`]: {
      color: theme.palette.primary.main,

      [`&.${stepLabelClasses.iconContainer}`]: {
        backgroundColor: theme.palette.primary.main,
      },
    },
  },

  [`& .${stepConnectorClasses.root}`]: {
    display: 'none',
  },
}));

const StepsNav: React.FC<BoxProps> = ({ ...props }) => {
  const { steps, currentStep, setCurrentStep } =
    React.useContext(FormikContext);

  const handleStep = (step: number, subStep: number) => {
    setCurrentStep({ step, subStep });
  };

  // console.log(steps);
  return (
    <MuiStepperWrap {...props}>
      <MuiStepper activeStep={currentStep.step} orientation="vertical">
        {steps.map((_step, _index) => (
          <Step key={_index}>
            {/* <StepLabel onClick={() => handleStep(_index, 0)} error={false}>
              {_step.label}
            </StepLabel> */}
            <StepLabel
              onClick={() => handleStep(_index, 0)}
              error={_step?.hasError}
              StepIconComponent={StepIcon}
              StepIconProps={{
                // @ts-ignore
                isCompleted: _step?.isCompleted,
                currentStep: currentStep.step,
                index: _index + 1,
              }}
            >
              {_step.label}
            </StepLabel>
            {_step?.subSteps && _step?.subSteps.length > 0 && (
              <StepContent>
                <MuiStepper
                  activeStep={currentStep.subStep}
                  orientation="vertical"
                >
                  {_step?.subSteps.map((_step, _subIndex) => (
                    <Step key={_subIndex}>
                      <StepLabel onClick={() => handleStep(_index, _subIndex)}>
                        {_step.label}
                      </StepLabel>
                    </Step>
                  ))}
                </MuiStepper>
              </StepContent>
            )}
          </Step>
        ))}
      </MuiStepper>
    </MuiStepperWrap>
  );
};

export default StepsNav;

interface iStepIcon extends StepIconProps {
  isCompleted: boolean;
  index: number;
}

const StepIcon: React.FC<iStepIcon> = (props) => {
  const { index, isCompleted, error } = props;

  return (
    <>
      {!isCompleted && !error && (
        <svg
          className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiStepIcon-root css-1gf3xom-MuiSvgIcon-root-MuiStepIcon-root"
          focusable="false"
          aria-hidden="true"
          viewBox="0 0 24 24"
          style={{
            color: 'rgb(104, 115, 133)',
          }}
        >
          <circle cx="12" cy="12" r="12"></circle>
          <text
            className="MuiStepIcon-text css-96uokb-MuiStepIcon-text"
            x="12"
            y="12"
            textAnchor="middle"
            dominantBaseline="central"
          >
            {index}
          </text>
        </svg>
      )}
      {isCompleted && !error && (
        <svg
          className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium MuiStepIcon-root Mui-completed css-1gf3xom-MuiSvgIcon-root-MuiStepIcon-root"
          focusable="false"
          aria-hidden="true"
          viewBox="0 0 24 24"
          data-testid="CheckCircleIcon"
          style={{
            color: '#2e7d32',
          }}
        >
          <path d="M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm-2 17l-5-5 1.4-1.4 3.6 3.6 7.6-7.6L19 8l-9 9z"></path>
        </svg>
      )}
      {error && <Warning color="error" fontSize="large" />}
    </>
  );
};
